import { canUseDOM } from '@ps/web-core-utils';
import { init } from '@rematch/core';
import createLoadingPlugin from '@rematch/loading';
import persistPlugin from '@rematch/persist';
import createSelect from '@rematch/select';
import { pick } from 'lodash';
import { createTransform } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import type { DeepPartial } from 'utility-types';

import type { connect } from '@swf/core/api/connect';
import models from '@swf/core/models';

import authLoginMiddleware from './middleware/authLoginMiddleware';
import persistBetSlipStateMiddleware from './middleware/persistBetSlipStateMiddleware';
import createEffectPlugin from './plugins/effectPlugin';
import type { ModelConfig, RootState, Store } from './types';

export * from './types';

const plugins = [createSelect()];

if (canUseDOM) {
  plugins.push(
    createLoadingPlugin({
      whitelist: ['auth/login', 'auth/register', 'auth/verifySession', 'rewards/exchange']
    }),
    persistPlugin({
      storage,
      whitelist: window.__INITIAL_STATE__?.device?.embedded
        ? ['localSettings']
        : ['localSettings', 'session'],
      key: 'starsweb',
      throttle: 500,
      version: 1,
      transforms: [
        createTransform(
          (inboundState) => pick(inboundState, ['token']),
          (outboundState) => outboundState,
          { whitelist: ['session'] }
        )
      ]
    })
  );
}

const createStore = (
  initialState?: DeepPartial<RootState>,
  connection?: ReturnType<typeof connect>,
  {
    additionalModels,
    httpDomain,
    headers
  }: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    additionalModels?: Record<string, ModelConfig<any, any>>;
    httpDomain?: string;
    headers?: Record<string, string>;
  } = {}
): Store => {
  const store = init({
    name: 'starsweb',
    models: { ...models, ...additionalModels },
    plugins: [...plugins, createEffectPlugin({ connection, httpDomain, headers })],
    redux: {
      initialState,
      middlewares: [authLoginMiddleware, persistBetSlipStateMiddleware]
    }
  }) as unknown as Store;
  store.select.getState = (state: RootState) => state;
  return store;
};

/**
 * @deprecated use store from context
 */
const store = createStore(canUseDOM ? window.__INITIAL_STATE__ : {});

const { dispatch, getState, select } = store;

export { createStore, select, dispatch, getState };

export default store;
